import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import IntakePage from "@/pages/IntakePage";
import ProjectPage from "@/pages/ProjectPage";
import InboxPage from "@/pages/InboxPage";
import WorkPage from "@/pages/WorkPage";
import PipelinePage from "@/pages/PipelinePage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerPage from "@/pages/CustomerPage";
import ProductsPage from "@/pages/ProductsPage";
import OversiktPage from "@/pages/OversiktPage";
import SettingsPage from "@/pages/SettingsPage";
import RatesSettingsPage from "@/pages/RatesSettingsPage";
import TextsSettingsPage from "@/pages/TextsSettingsPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import SourcePage from "@/pages/SourcePage";
import TilbudPage from "@/pages/TilbudPage";
import TilbudProposalPage from "@/pages/TilbudProposalPage";
import TilbudStillPage from "@/pages/TilbudStillPage";
import PublicQuotePage from "@/pages/PublicQuotePage";
import PublicClarificationPage from "@/pages/PublicClarificationPage";
import PublicIntakePage from "@/pages/PublicIntakePage";
import PublicIntakeStatusPage from "@/pages/PublicIntakeStatusPage";
import { LosMark } from "@/components/Logo";
import "@/App.css";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-5 bg-background" data-testid="app-loading">
        <LosMark className="h-10 w-10 fade-up" />
        <div className="flex items-center gap-2.5 text-[13px] text-muted-foreground">
          <span className="h-1.5 w-1.5 rounded-full bg-primary pulse-dot" /> Laster Skarpenord
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (user.must_change_password && loc.pathname !== "/passord") return <Navigate to="/passord" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const el = document.getElementById("main-scroll");
    if (el) el.scrollTo({ top: 0 });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <TooltipProvider delayDuration={200}>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Kvitebergs tilbud til Skarpenord – offentlig, norsk */}
            <Route path="/tilbud" element={<TilbudProposalPage />} />
            <Route path="/tilbud/still/:key" element={<TilbudStillPage />} />
            {/* Customer-facing pages */}
            <Route path="/q/:token" element={<PublicQuotePage />} />
            <Route path="/avklaring/:token" element={<PublicClarificationPage />} />
            <Route path="/foresporsel" element={<PublicIntakePage />} />
            <Route path="/foresporsel/status/:token" element={<PublicIntakeStatusPage />} />
            <Route path="/passord" element={<RequireAuth><ChangePasswordPage /></RequireAuth>} />
            <Route path="/p/:id/kilde/:docId" element={<RequireAuth><SourcePage /></RequireAuth>} />
            <Route path="/p/:id/tilbud/:rev" element={<RequireAuth><TilbudPage /></RequireAuth>} />
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route path="/" element={<OversiktPage />} />
              <Route path="/ny" element={<IntakePage />} />
              <Route path="/ny/:sid" element={<IntakePage />} />
              {/* The canvas - fills the shell's main area edge to edge */}
              <Route path="/p/:id" element={<ProjectPage />} />
              <Route path="/innboks" element={<InboxPage />} />
              <Route path="/innboks/:key" element={<InboxPage />} />
              <Route path="/foresporsler" element={<Navigate to="/innboks" replace />} />
              <Route path="/arbeid" element={<WorkPage />} />
              <Route path="/arbeidsomrader" element={<Navigate to="/arbeid" replace />} />
              <Route path="/pipeline" element={<PipelinePage />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/customers/:id" element={<CustomerPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/insights" element={<Navigate to="/" replace />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/satser" element={<RatesSettingsPage />} />
              <Route path="/settings/tekster" element={<TextsSettingsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" closeButton toastOptions={{ classNames: { toast: "group toast rounded-xl border border-black/[0.08] bg-white text-foreground shadow-float", title: "text-[13.5px] font-semibold", description: "text-[12.5px] text-muted-foreground", success: "[&_[data-icon]]:text-[hsl(var(--status-ok))]", error: "[&_[data-icon]]:text-[hsl(var(--status-bad))]", warning: "[&_[data-icon]]:text-[hsl(var(--status-warn))]", info: "[&_[data-icon]]:text-primary", closeButton: "bg-white border-black/[0.08] text-muted-foreground hover:text-foreground" } }} />
      </TooltipProvider>
    </AuthProvider>
  );
}

export default App;
